import React, {useState, useEffect} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import {toTime, toTimeType, timeConverter, isObjectEmpty, uploadCSV, replaceAll, timeConverterSM} from '../../../Utils';
import Spinner from '../../common/generic/Spinner';

const ContainerComp = ({pageState, reportState, csvData, setcsvData}) =>{
    const {database, deleteThis, request, fetchModules, modulesData, modulesReady } = pageState;
    const [processedMarks, setProcessedMarks] = useState({});
    const [markRecords, setMarkRecords] = useState({});
    const {selectedStudents, selectedProgram} = reportState;

    /*
    CSV NOTES
    =========
    There is a big problem when trying to upload more then 1 csv file.  The below system will reset all the data in 
    this component and start it over when when the user clicks save.
    
    Steps
    1) User clicks save in either ProgramReportTableComp or AcitivityReportComp
        1b) openCSVPage() will populate csvData using setcsvData({processStage:'newRequest'});
    2) ViewReportsPage checks to see if the marks have changed (* seems to be called way to many times...)
        2b) setReportType(csvData.reportType);
            setSelectedProgram({...csvData.selectedProgram});
            setSelectedStudents([...csvData.selectedStudents]); 
    3) Start the report
        3a) CSV Step 1: checks if a new request for csv and starts to clear the data
        3b) CSV Step 2: clears the data 
        3c) CSV Step 3: keep checking to see when the data has been cleared
        3d) CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
        3e) (The normal Startup starts at CSV Step 1) 
    */

    // CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
    // The normal Startup starts at CSV Step 1 
    const tryLoadingCSVStage2 = () => {
        if(!csvData) return;
        if(csvData.processStage === 'clearingData'){
            if(!database['getPretestModuleMarks'] && isObjectEmpty(markRecords) && isObjectEmpty(processedMarks)){
                setcsvData({...csvData, processStage:'clearedData'});
            }
        }
    };
    // CSV Step 3: keep checking to see when the data has been cleared
    useEffect(() =>{tryLoadingCSVStage2();},[database['getPretestModuleMarks']]);
    useEffect(() =>{tryLoadingCSVStage2();},[markRecords]);
    useEffect(() =>{tryLoadingCSVStage2();},[processedMarks]);

    // CSV Step 2: clears the data
    useEffect(() => {
        if(!csvData) return;

        if(csvData.processStage === 'clearingData'){
            deleteThis('getPretestModuleMarks');
            setMarkRecords({});
            setProcessedMarks({});
        }
    },[csvData]);

    // CSV Step 1: checks if a new request for csv and starts to clear the data 
    // Normal Startup Step 1:
    useEffect(() => {
        if(csvData){
            if(csvData.processStage==='newRequest'){
                // csvData.setloadingCSV({...csvData.loadingCSV, pretest:true});
                setcsvData({...csvData, processStage:'clearingData'});
                return;
            }

            if(csvData.processStage==='clearingData') return;
        }

        if(!isObjectEmpty(markRecords)) return; // already loading

        if(!database['getPretestModuleMarks']){
            let studentQueries = selectedStudents.map(studentRec=>{
                let getMarksQuery={
                    params: studentRec.id+'~'+selectedProgram.name,
                    func:'getPretestModuleMarks'            
                };
                return getMarksQuery;
            });

            if(studentQueries.length > 0){
                let counter = 1;
                studentQueries = studentQueries.map((query,i)=>{
                    let prevQuery = (i!=0) ? studentQueries[i-1] : 'undefinded';
                    if(prevQuery !== 'undefinded'){
                        counter++;
                        query.nextRequest = prevQuery;
                    }
                    return query;
                });
                let loadChainedMarks = studentQueries[studentQueries.length-1];
                loadChainedMarks.numQueries = counter;
                setMarkRecords({info:{numStudents:counter,numLoaded:counter}});

                // pass the link list of saved changes into the database
                request(loadChainedMarks); 
            }
            else
                console.log('Nothing to load?');
        }
    });

    // convert database['getPretestModuleMarks'] into setMarkRecords
    // UDATES - when database['getPretestModuleMarks'] changes
    useEffect(() => {
        if(csvData && csvData.processStage !== 'clearedData') return; // don't setup markRecords for csv files without being ready 
        if(isObjectEmpty(markRecords)) return;
        (!pageState.isFetchingModules && !modulesReady) && fetchModules(selectedProgram.id.toLowerCase());
        if(!database['getPretestModuleMarks']) return;
        
        let marks = {};
        let key = 'unavailable';
        let counter = {...markRecords.info};

        if(database['getPretestModuleMarks'].isLoaded) counter.numLoaded--;
        
        marks = {
            isloaded: database['getPretestModuleMarks'].isLoaded,
            data: database['getPretestModuleMarks'].data
        }; 

        if((marks.data) && !isObjectEmpty(marks.data)){
            key = Object.keys(marks.data)[0].split('+')[0];
        }

        setMarkRecords({...markRecords, info:{...counter}, [key]:marks});

    }, [database['getPretestModuleMarks']]); 

    const tryLoadingNow = () => {
        if(isObjectEmpty(markRecords)) return;
        if(!isObjectEmpty(processedMarks)) return;
        if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return;

        let reportData = JSON.parse(localStorage.getItem('reportData'));
        let masterLevel = parseInt(reportData.masterLevel)/100;

        if(modulesReady) { 
            let studentsModuleMarks = selectedStudents.reduce((acc,studentRec)=>{
                let studentID = studentRec.id;
                let studentMarkRecords = markRecords[studentID];
                if(!studentMarkRecords) return acc; // still loading marks

                let marksPerModule = modulesData.map(moduleRec => { // foreach module
                    let moduleID = moduleRec.moduleID;
                    if(studentMarkRecords.data[`${studentID}+${selectedProgram.name}+${moduleID[0]}`]) {
                        return {moduleID, mark: studentMarkRecords.data[`${studentID}+${selectedProgram.name}+${moduleID[0]}`]};
                    }

                }).filter(e=> e);

                acc[`${studentRec.id}+${studentRec.name}`] = marksPerModule;
                return acc;
            },{});
            setProcessedMarks(studentsModuleMarks);

            if(csvData){
                let newLine = '%0A';
                let csvString = '';
                csvString =`"If the student has a grade less then 80%, that module will be assigned to them."${newLine}`;
                csvString +=`"Students without marks will not appear in this report"${newLine}`;
                let tableHeaderStr = '"Modules Tested","Questions Correct","Num of Questions","Score","Time","Assigned to Student"'+newLine;

                Object.keys(studentsModuleMarks).forEach((key) => { 
                    let headerStr = `${newLine}"${key.split('+')[1]}"${newLine}"${selectedProgram.name}"${newLine}"Pretest Report"${newLine}`;
                    let tableData  = studentsModuleMarks[key];

                    csvString += headerStr + tableHeaderStr;
                    tableData.forEach((moduleRec) => {
                        const { moduleID, mark} = moduleRec;
                        const { QuestionsRight, QuestionsTotal, Duration} = mark;
                        let recordStr = `"${moduleID[1]}","${QuestionsRight}","${QuestionsTotal}","${Math.round((QuestionsRight/QuestionsTotal)*100)}%","${toTime(Duration)} ${toTimeType(Duration)}","${((QuestionsRight/QuestionsTotal) >= masterLevel)? 'No' : 'Yes'}"${newLine}`;
                        csvString += recordStr;
                    });

                    let footer = '"If the student has a grade less then 80%, that module will be assigned to them."';
                    csvString += footer;
                });
                                
                let name = '';
                if(Object.keys(studentsModuleMarks).length===0){
                    window.alert('There are no marks for the student(s) selected');
                    csvData.setloadingCSV({...csvData.loadingCSV, pretest:false});
                    return;
                }
                if(Object.keys(studentsModuleMarks).length==1)
                    name = Object.keys(studentsModuleMarks)[0].split('+')[1]+'_';

                let today = new Date();
                today = timeConverterSM(today.getTime());

                uploadCSV(name+'PretestReport_'+selectedProgram.name+'_'+today,csvString);
                //uploadCSV('pretestReport',csvString);
                csvData.setloadingCSV({...csvData.loadingCSV, pretest:false});
            }
        };
    }; 
    useEffect(() =>{tryLoadingNow();},[modulesData]);
    useEffect(() =>{tryLoadingNow();},[markRecords]);

    if(csvData) return <></>;
    if(isObjectEmpty(markRecords)) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;
    if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;

    return <>
        {Object.keys(processedMarks).length < selectedStudents.length && <center> <p>  Students without marks will not appear in this report </p></center>}
        {Object.keys(processedMarks).map((key, i) => ( 
            <span key={i}><br/>  
            <PretestReportComp header={{student: key.split('+')[1], program: selectedProgram.name}} tableData={processedMarks[key]}/>            
            <br/></span>
        ))}
    </>;
};

const PretestReportComp = ({header, tableData}) => {
    const [masterLevel, setMasterLevel] = useState(0.8);

    useEffect(()=>{
        let reportData = JSON.parse(localStorage.getItem('reportData'));
        if(masterLevel !== (parseInt(reportData.masterLevel)/100))
            setMasterLevel(parseInt(reportData.masterLevel)/100);
    });

    const titleRow = <Row style={{ fontSize:'0.9em', fontWeight:'bold'}}>
    <Col xs='5'><Row>
        <Col style={{textAlign: 'left'}} xs='12'> Modules Tested </Col>
        </Row>
    </Col>
    <Col xs='7'><Row>
        <Col style={{textAlign: 'center'}} xs='3'>Questions Correct</Col>
        <Col style={{textAlign: 'center'}} xs='2'># of Questions</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Score</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Time</Col>
        <Col style={{textAlign: 'center'}} xs='3'>Assigned to Student</Col>
        </Row></Col>
    </Row>;

    const headerComp = <center>
        <h4><Row>
            <Col xs='4'>{header.student}</Col> 
            <Col xs='4'>{header.program} </Col>
            <Col xs='4'>Pre-test Report</Col>
        </Row></h4>
    </center>;
    
    const footer = <center>
        <hr/>If the student has a grade less then {masterLevel*100}%, that module will be assigned to them. 
    </center>;

    return (
        <>
            {headerComp}
            <hr/>
            {titleRow}
            {tableData.map((moduleRec, module_i) => <span key={module_i}>
                <TableRow key={module_i} data={moduleRec} masterLevel={masterLevel} fontWeight={'normal'}/>
                </span>
            )} 
            {footer}
        </>
    );
};
PretestReportComp.propTypes = {
    tableData: PropTypes.array
};

const TableRow = (props) => {
    const { masterLevel } = props;
    const { moduleID, mark} = props.data;
    const { QuestionsRight, QuestionsTotal, Duration} = mark;

    return (
        <Row style={{fontSize:'0.9em'}}>
            {(QuestionsTotal!=='-1')?<>
                <Col xs='5'><Row>
                    <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='12'>{moduleID[1]}</Col>
                    </Row>
                </Col>
                <Col xs='7'><Row>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'>{QuestionsRight}</Col>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{QuestionsTotal}</Col>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{Math.round((QuestionsRight/QuestionsTotal)*100)}%</Col>                
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{toTime(Duration)} {toTimeType(Duration)}</Col>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'>{((QuestionsRight/QuestionsTotal) >= masterLevel)? 'No' : 'Yes'}</Col>
                </Row></Col></>
            :<>
            <Col xs='5'><Row>
                    <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='12'>In Progress...</Col>
                    </Row>
                </Col>
                <Col xs='7'><Row>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'> -- </Col>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'> -- </Col>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'> -- </Col>                
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'> -- </Col>
                    <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'> -- </Col>
            </Row></Col></>}
        </Row>
    );
};

TableRow.propTypes = {
    fontWeight: PropTypes.string,
    data: PropTypes.object
};

export default ContainerComp;